import type { DisplayOrderUpdateInput } from '@/features/board/adminOrdering';
import { createInitialMockCatalogItems } from '@/features/content/mockCatalogSeed';
import { createInitialMockCertificationAssets } from '@/features/content/mockCertificationSeed';
import { createInitialMockHistoryEntries } from '@/features/content/mockHistorySeed';
import type {
  AdminCatalogItemDetail,
  AdminCatalogItemSummary,
  AdminCertificationAssetDetail,
  AdminCertificationAssetSummary,
  AdminHistoryEntryDetail,
  AdminHistoryEntrySummary,
  CatalogItemDetail,
  CatalogItemMutationInput,
  CatalogItemPreviewImage,
  CatalogItemSummary,
  CertificationAssetMutationInput,
  CertificationAssetResponse,
  HistoryEntryMutationInput,
  HistoryEntryResponse,
} from '@/features/content/types';
import { buildMockStorageKey, mockS3Prefixes } from '@/features/mock/storage';
import type { CatalogCategorySlug } from '@/routes/routeRegistry';

type MockHistoryEntry = AdminHistoryEntryDetail;

type MockCertificationAsset = AdminCertificationAssetDetail;

type MockCatalogItem = AdminCatalogItemDetail;

const getCatalogThumbnailImageUrl = (item: MockCatalogItem) => {
  return (
    item.previewImages.at(item.mainPreviewImageIndex)?.imageUrl ??
    item.previewImages.at(0)?.imageUrl ??
    item.detailImageUrl
  );
};

const createSlug = (value: string) => {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9가-힣\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

const sortByDisplayOrder = <T extends { displayOrder: number; publishedAt: string }>(
  items: T[],
) => {
  return [...items].sort((left, right) => {
    if (left.displayOrder !== right.displayOrder) {
      return left.displayOrder - right.displayOrder;
    }

    return new Date(right.publishedAt).getTime() - new Date(left.publishedAt).getTime();
  });
};

const sortCertificationAssets = <
  T extends {
    category: MockCertificationAsset['category'];
    displayOrder: number;
    publishedAt: string;
  },
>(
  items: T[],
) => {
  return [...items].sort((left, right) => {
    if (left.category !== right.category) {
      return left.category.localeCompare(right.category);
    }

    if (left.displayOrder !== right.displayOrder) {
      return left.displayOrder - right.displayOrder;
    }

    return new Date(right.publishedAt).getTime() - new Date(left.publishedAt).getTime();
  });
};

const getNextId = (items: Array<{ id: number }>) => {
  return Math.max(0, ...items.map((item) => item.id)) + 1;
};

const getNextDisplayOrder = (items: Array<{ displayOrder: number }>) => {
  return Math.max(-1, ...items.map((item) => item.displayOrder)) + 1;
};

const resolveMock = <T>(value: T): Promise<T> => {
  return Promise.resolve(value);
};

const rejectMock = <T = never>(message: string): Promise<T> => {
  return Promise.reject(new Error(message));
};

const revokeObjectUrl = (url: string) => {
  if (url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

const buildHistoryImage = (file: File | null, current?: MockHistoryEntry) => {
  if (!file && current) {
    return {
      imageContentType: current.imageContentType,
      imageFileName: current.imageFileName,
      imageFileSize: current.imageFileSize,
      imageUrl: current.imageUrl,
    };
  }

  if (!file) {
    return {
      imageContentType: '',
      imageFileName: '',
      imageFileSize: 0,
      imageUrl: '',
    };
  }

  return {
    imageContentType: file.type || 'image/jpeg',
    imageFileName: buildMockStorageKey(mockS3Prefixes.historyImages, file.name),
    imageFileSize: file.size,
    imageUrl: URL.createObjectURL(file),
  };
};

const buildCertificationImage = (file: File | null, current?: MockCertificationAsset) => {
  if (!file && current) {
    return {
      imageContentType: current.imageContentType,
      imageFileName: current.imageFileName,
      imageFileSize: current.imageFileSize,
      imageUrl: current.imageUrl,
    };
  }

  if (!file) {
    throw new Error('인증·특허 이미지를 선택해 주세요.');
  }

  return {
    imageContentType: file.type || 'image/jpeg',
    imageFileName: buildMockStorageKey(mockS3Prefixes.certificationImages, file.name),
    imageFileSize: file.size,
    imageUrl: URL.createObjectURL(file),
  };
};

const buildCatalogPreviewImages = (
  files: File[],
  current?: MockCatalogItem,
): CatalogItemPreviewImage[] => {
  if (files.length === 0 && current) {
    return current.previewImages.map((image) => ({ ...image }));
  }

  if (files.length === 0) {
    throw new Error('사업영역 미리보기 이미지를 1장 이상 선택해 주세요.');
  }

  return files.map((file, index) => ({
    id: index + 1,
    fileName: buildMockStorageKey(mockS3Prefixes.catalogPreviewImages, file.name),
    imageUrl: URL.createObjectURL(file),
    contentType: file.type || 'image/jpeg',
    fileSize: file.size,
    displayOrder: index + 1,
  }));
};

const buildCatalogDetailImage = (file: File | null, current?: MockCatalogItem) => {
  if (!file && current) {
    return {
      detailImageContentType: current.detailImageContentType,
      detailImageFileName: current.detailImageFileName,
      detailImageFileSize: current.detailImageFileSize,
      detailImageUrl: current.detailImageUrl,
    };
  }

  if (!file) {
    throw new Error('사업영역 상세 이미지를 선택해 주세요.');
  }

  return {
    detailImageContentType: file.type || 'image/jpeg',
    detailImageFileName: buildMockStorageKey(mockS3Prefixes.catalogDetailImages, file.name),
    detailImageFileSize: file.size,
    detailImageUrl: URL.createObjectURL(file),
  };
};

const buildCatalogDetailContent = (
  input: CatalogItemMutationInput,
  current?: MockCatalogItem,
): CatalogItemMutationInput['detailContent'] => {
  const detailContent = structuredClone(input.detailContent);

  if (input.additionalDetailImage) {
    if (current?.detailContent?.additionalDetailImage?.src) {
      revokeObjectUrl(current.detailContent.additionalDetailImage.src);
    }

    detailContent.additionalDetailImage = {
      ...(detailContent.additionalDetailImage ?? { alt: '추가 상세 이미지' }),
      src: URL.createObjectURL(input.additionalDetailImage),
    };
  } else if (
    !detailContent.additionalDetailImage &&
    current?.detailContent?.additionalDetailImage?.src
  ) {
    revokeObjectUrl(current.detailContent.additionalDetailImage.src);
  }

  return detailContent;
};

let mockHistoryEntries: MockHistoryEntry[] = createInitialMockHistoryEntries();

let mockCertificationAssets: MockCertificationAsset[] = createInitialMockCertificationAssets();

let mockCatalogItems: MockCatalogItem[] = createInitialMockCatalogItems();

const toHistorySummary = (entry: MockHistoryEntry): HistoryEntryResponse => {
  return {
    id: entry.id,
    year: entry.year,
    title: entry.title,
    description: entry.description,
    imageUrl: entry.imageUrl,
    imageFit: entry.imageFit ?? 'contain',
    imageObjectPosition: entry.imageObjectPosition ?? 'center center',
    imageScale: entry.imageScale ?? 1,
    displayOrder: entry.displayOrder,
    publishedAt: entry.publishedAt,
  };
};

const toAdminHistorySummary = (entry: MockHistoryEntry): AdminHistoryEntrySummary => {
  return {
    ...toHistorySummary(entry),
    isPublished: entry.isPublished,
  };
};

const toCertificationSummary = (entry: MockCertificationAsset): CertificationAssetResponse => {
  return {
    id: entry.id,
    category: entry.category,
    title: entry.title,
    acquiredYear: entry.acquiredYear,
    imageUrl: entry.imageUrl,
    imageFit: entry.imageFit ?? 'contain',
    imageObjectPosition: entry.imageObjectPosition ?? 'center center',
    imageScale: entry.imageScale ?? 1,
    displayOrder: entry.displayOrder,
    publishedAt: entry.publishedAt,
  };
};

const toAdminCertificationSummary = (
  entry: MockCertificationAsset,
): AdminCertificationAssetSummary => {
  return {
    ...toCertificationSummary(entry),
    isPublished: entry.isPublished,
    translations: entry.translations ?? [],
  };
};

const toAdminCatalogSummary = (item: MockCatalogItem): AdminCatalogItemSummary => {
  return {
    id: item.id,
    category: item.category,
    title: item.title,
    slug: item.slug,
    thumbnailImageUrl: getCatalogThumbnailImageUrl(item),
    previewImageCount: item.previewImages.length,
    displayOrder: item.displayOrder,
    isPublished: item.isPublished,
    publishedAt: item.publishedAt,
  };
};

const toCatalogSummary = (item: MockCatalogItem): CatalogItemSummary => {
  return {
    id: item.id,
    category: item.category,
    title: item.title,
    slug: item.slug,
    summary: item.summary,
    model: item.detailContent?.heroSection?.model ?? '',
    tags: item.detailContent?.heroSection?.tags ?? [],
    thumbnailImageUrl: getCatalogThumbnailImageUrl(item),
    displayOrder: item.displayOrder,
    publishedAt: item.publishedAt,
  };
};

const cloneCatalogDetail = (item: MockCatalogItem): AdminCatalogItemDetail => {
  return {
    ...item,
    previewImages: item.previewImages.map((image) => ({ ...image })),
  };
};

export const fetchMockHistoryEntries = (): Promise<HistoryEntryResponse[]> => {
  return resolveMock(
    sortByDisplayOrder(mockHistoryEntries)
      .filter((entry) => entry.isPublished)
      .map(toHistorySummary),
  );
};

export const fetchMockAdminHistoryEntries = (): Promise<AdminHistoryEntrySummary[]> => {
  return resolveMock(sortByDisplayOrder(mockHistoryEntries).map(toAdminHistorySummary));
};

export const fetchMockAdminHistoryEntry = (
  historyEntryId: number,
): Promise<AdminHistoryEntryDetail> => {
  const entry = mockHistoryEntries.find((item) => item.id === historyEntryId);

  if (!entry) {
    return rejectMock('연혁 항목을 찾지 못했습니다.');
  }

  return resolveMock({ ...entry });
};

export const createMockHistoryEntry = (
  input: HistoryEntryMutationInput,
): Promise<AdminHistoryEntryDetail> => {
  const image = buildHistoryImage(input.image);
  const entry: MockHistoryEntry = {
    id: getNextId(mockHistoryEntries),
    year: input.year,
    title: input.title,
    description: input.description,
    imageFit: input.imageFit,
    imageObjectPosition: input.imageObjectPosition,
    imageScale: input.imageScale,
    displayOrder: getNextDisplayOrder(mockHistoryEntries),
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    ...image,
  };

  mockHistoryEntries = [entry, ...mockHistoryEntries];
  return resolveMock({ ...entry });
};

export const updateMockHistoryEntry = (
  historyEntryId: number,
  input: HistoryEntryMutationInput,
): Promise<AdminHistoryEntryDetail> => {
  const current = mockHistoryEntries.find((item) => item.id === historyEntryId);

  if (!current) {
    return rejectMock('연혁 항목을 찾지 못했습니다.');
  }

  const nextImage = buildHistoryImage(input.image, current);

  if (input.image && nextImage.imageUrl !== current.imageUrl) {
    revokeObjectUrl(current.imageUrl);
  }

  const updated: MockHistoryEntry = {
    ...current,
    year: input.year,
    title: input.title,
    description: input.description,
    imageFit: input.imageFit,
    imageObjectPosition: input.imageObjectPosition,
    imageScale: input.imageScale,
    displayOrder: current.displayOrder,
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    ...nextImage,
  };

  mockHistoryEntries = mockHistoryEntries.map((item) => {
    return item.id === historyEntryId ? updated : item;
  });

  return resolveMock({ ...updated });
};

export const deleteMockHistoryEntry = (historyEntryId: number): Promise<void> => {
  const current = mockHistoryEntries.find((item) => item.id === historyEntryId);

  if (current) {
    revokeObjectUrl(current.imageUrl);
  }

  mockHistoryEntries = mockHistoryEntries.filter((item) => item.id !== historyEntryId);
  return resolveMock(undefined);
};

export const updateMockHistoryEntryDisplayOrders = (
  items: DisplayOrderUpdateInput[],
): Promise<void> => {
  const nextDisplayOrders = new Map(items.map((item) => [item.id, item.displayOrder]));

  mockHistoryEntries = mockHistoryEntries.map((entry) => {
    const nextDisplayOrder = nextDisplayOrders.get(entry.id);
    return nextDisplayOrder === undefined ? entry : { ...entry, displayOrder: nextDisplayOrder };
  });

  return resolveMock(undefined);
};

export const fetchMockCertificationAssets = (): Promise<CertificationAssetResponse[]> => {
  return resolveMock(
    sortCertificationAssets(mockCertificationAssets)
      .filter((entry) => entry.isPublished)
      .map(toCertificationSummary),
  );
};

export const fetchMockAdminCertificationAssets = (): Promise<AdminCertificationAssetSummary[]> => {
  return resolveMock(
    sortCertificationAssets(mockCertificationAssets).map(toAdminCertificationSummary),
  );
};

export const fetchMockAdminCertificationAsset = (
  certificationAssetId: number,
): Promise<AdminCertificationAssetDetail> => {
  const entry = mockCertificationAssets.find((item) => item.id === certificationAssetId);

  if (!entry) {
    return rejectMock('인증·특허 항목을 찾지 못했습니다.');
  }

  return resolveMock({ ...entry });
};

export const createMockCertificationAsset = (
  input: CertificationAssetMutationInput,
): Promise<AdminCertificationAssetDetail> => {
  const image = buildCertificationImage(input.image);
  const entry: MockCertificationAsset = {
    id: getNextId(mockCertificationAssets),
    category: input.category,
    title: input.title,
    acquiredYear: input.acquiredYear,
    imageFit: input.imageFit,
    imageObjectPosition: input.imageObjectPosition,
    imageScale: input.imageScale,
    displayOrder: input.displayOrder,
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    translations: input.translations ?? [],
    ...image,
  };

  mockCertificationAssets = [entry, ...mockCertificationAssets];
  return resolveMock({ ...entry });
};

export const updateMockCertificationAsset = (
  certificationAssetId: number,
  input: CertificationAssetMutationInput,
): Promise<AdminCertificationAssetDetail> => {
  const current = mockCertificationAssets.find((item) => item.id === certificationAssetId);

  if (!current) {
    return rejectMock('인증·특허 항목을 찾지 못했습니다.');
  }

  const nextImage = buildCertificationImage(input.image, current);

  if (input.image && nextImage.imageUrl !== current.imageUrl) {
    revokeObjectUrl(current.imageUrl);
  }

  const updated: MockCertificationAsset = {
    ...current,
    category: input.category,
    title: input.title,
    acquiredYear: input.acquiredYear,
    imageFit: input.imageFit,
    imageObjectPosition: input.imageObjectPosition,
    imageScale: input.imageScale,
    displayOrder: input.displayOrder,
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    translations: input.translations ?? [],
    ...nextImage,
  };

  mockCertificationAssets = mockCertificationAssets.map((item) => {
    return item.id === certificationAssetId ? updated : item;
  });

  return resolveMock({ ...updated });
};

export const deleteMockCertificationAsset = (certificationAssetId: number): Promise<void> => {
  const current = mockCertificationAssets.find((item) => item.id === certificationAssetId);

  if (current) {
    revokeObjectUrl(current.imageUrl);
  }

  mockCertificationAssets = mockCertificationAssets.filter(
    (item) => item.id !== certificationAssetId,
  );
  return resolveMock(undefined);
};

export const fetchMockCatalogItem = (
  category: CatalogCategorySlug,
  slug: string,
): Promise<CatalogItemDetail> => {
  const item = mockCatalogItems.find(
    (entry) => entry.category === category && entry.slug === slug && entry.isPublished,
  );

  if (!item) {
    return rejectMock('사업영역 항목을 찾지 못했습니다.');
  }

  return resolveMock(cloneCatalogDetail(item));
};

export const fetchMockCatalogItems = (
  category: CatalogCategorySlug,
): Promise<CatalogItemSummary[]> => {
  return resolveMock(
    sortByDisplayOrder(mockCatalogItems)
      .filter((item) => item.category === category && item.isPublished)
      .map(toCatalogSummary),
  );
};

export const fetchMockAdminCatalogItems = (
  category?: CatalogCategorySlug,
): Promise<AdminCatalogItemSummary[]> => {
  return resolveMock(
    sortByDisplayOrder(mockCatalogItems)
      .filter((item) => (category ? item.category === category : true))
      .map(toAdminCatalogSummary),
  );
};

export const fetchMockAdminCatalogItem = (
  catalogItemId: number,
): Promise<AdminCatalogItemDetail> => {
  const item = mockCatalogItems.find((entry) => entry.id === catalogItemId);

  if (!item) {
    return rejectMock('사업영역 항목을 찾지 못했습니다.');
  }

  return resolveMock(cloneCatalogDetail(item));
};

export const createMockCatalogItem = (
  input: CatalogItemMutationInput,
): Promise<AdminCatalogItemDetail> => {
  const previewImages = buildCatalogPreviewImages(input.previewImages);
  const detailImage = buildCatalogDetailImage(input.detailImage);
  const item: MockCatalogItem = {
    id: getNextId(mockCatalogItems),
    category: input.category,
    title: input.title,
    slug: createSlug(input.title),
    summary: input.summary,
    detailContent: buildCatalogDetailContent(input),
    displayOrder: getNextDisplayOrder(
      mockCatalogItems.filter((currentItem) => currentItem.category === input.category),
    ),
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    mainPreviewImageIndex: input.mainPreviewImageIndex,
    previewImages,
    ...detailImage,
  };

  mockCatalogItems = [item, ...mockCatalogItems];
  return resolveMock(cloneCatalogDetail(item));
};

export const updateMockCatalogItem = (
  catalogItemId: number,
  input: CatalogItemMutationInput,
): Promise<AdminCatalogItemDetail> => {
  const current = mockCatalogItems.find((item) => item.id === catalogItemId);

  if (!current) {
    return rejectMock('사업영역 항목을 찾지 못했습니다.');
  }

  const nextPreviewImages = input.removePreviewImages
    ? []
    : buildCatalogPreviewImages(input.previewImages, current);
  const nextDetailImage = buildCatalogDetailImage(input.detailImage, current);

  if (input.previewImages.length > 0 || input.removePreviewImages) {
    current.previewImages.forEach((image) => {
      revokeObjectUrl(image.imageUrl);
    });
  }

  if (input.detailImage && nextDetailImage.detailImageUrl !== current.detailImageUrl) {
    revokeObjectUrl(current.detailImageUrl);
  }

  const updated: MockCatalogItem = {
    ...current,
    category: input.category,
    title: input.title,
    slug: createSlug(input.title),
    summary: input.summary,
    detailContent: buildCatalogDetailContent(input, current),
    displayOrder: current.displayOrder,
    isPublished: input.isPublished,
    publishedAt: input.publishedAt,
    mainPreviewImageIndex:
      nextPreviewImages.length > 0
        ? Math.min(input.mainPreviewImageIndex, nextPreviewImages.length - 1)
        : 0,
    previewImages: nextPreviewImages,
    ...nextDetailImage,
  };

  mockCatalogItems = mockCatalogItems.map((item) => {
    return item.id === catalogItemId ? updated : item;
  });

  return resolveMock(cloneCatalogDetail(updated));
};

export const deleteMockCatalogItem = (catalogItemId: number): Promise<void> => {
  const current = mockCatalogItems.find((item) => item.id === catalogItemId);

  if (current) {
    current.previewImages.forEach((image) => {
      revokeObjectUrl(image.imageUrl);
    });
    revokeObjectUrl(current.detailImageUrl);
    if (current.detailContent?.additionalDetailImage?.src) {
      revokeObjectUrl(current.detailContent.additionalDetailImage.src);
    }
  }

  mockCatalogItems = mockCatalogItems.filter((item) => item.id !== catalogItemId);
  return resolveMock(undefined);
};

export const updateMockCatalogItemDisplayOrders = (
  items: DisplayOrderUpdateInput[],
): Promise<void> => {
  const nextDisplayOrders = new Map(items.map((item) => [item.id, item.displayOrder]));

  mockCatalogItems = mockCatalogItems.map((item) => {
    const nextDisplayOrder = nextDisplayOrders.get(item.id);
    return nextDisplayOrder === undefined ? item : { ...item, displayOrder: nextDisplayOrder };
  });

  return resolveMock(undefined);
};
